/**
 * AccountPricingPage
 *
 * Lets users manage subscription: pick plan/frequency, go to Stripe Checkout
 * or Billing Portal, and shows current plan info. Handles return from Stripe.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BadgeCheck } from 'lucide-react';
import { useAuth } from '../../context/auth';
import { auth } from '../../firebase/firebase';
import { 
  createCheckoutSession, 
  redirectToCheckout, 
  redirectToBillingPortal,
  setStripePublicKeyForUser,
  initializeStripe
} from '../../services/stripeService';
import { syncSubscriptionData } from '../../services/subscriptionSyncService';
import FrequencyToggle from '../../components/auth/pricing/FrequencyToggle';
import { ButtonSpinner } from '../../components/ui/feedback/loading-spinner.jsx';
import Footer from '../../components/shared/Footer';
import pricingData, { ORIGINAL_PRICING, PLAN_DISCOUNTS, ANNUAL_TOTAL } from '../../data/pricing/pricingPlans';
import { kevinMeetingUrl } from '../../data/scheduling/schedulingLinks';
import { classNames } from '../../components/auth/pricing/utils';
import { LoadingModal } from '../../components/ui/modals/loading-modal.jsx';
import { trackInitiateCheckout } from '../../utils/analytics/metaPixel';

  // Helper function to preload Stripe.js script and initialize Stripe with correct key per user
  const preloadStripeScript = (user) => {
  if (document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
    console.log('Stripe script is already loaded');
    // AccountPricingPage: Set key based on user's cohort (old users vs new users)
    // This is different from PricingPage which ALWAYS uses full_pac keys
    setStripePublicKeyForUser(user);
    return;
  }
  
  console.log('Preloading Stripe.js script');
  
    // CRITICAL: AccountPricingPage uses different logic than PricingPage
    // Here we check user's postAcquisitionUser flag to support BOTH old and new users
    // - Old users (without flag): Use legacy Stripe account
    // - New users (with flag): Use full_pac Stripe account
    setStripePublicKeyForUser(user);
    const stripePublicKey = (typeof window !== 'undefined' && window.__STRIPE_PK_OVERRIDE) || import.meta.env.VITE_STRIPE_PUBLIC_KEY;
  
  if (!stripePublicKey) {
    console.error('VITE_STRIPE_PUBLIC_KEY is not set in environment variables');
    return;
  }
  
  // Create and append the script element
  const script = document.createElement('script');
  script.src = 'https://js.stripe.com/v3/';
  script.async = true;
  document.head.appendChild(script);
};

// Function to format date - moved to top level
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // Handle Firestore timestamp objects that might come as objects with _seconds and _nanoseconds
  if (typeof dateString === 'object' && dateString._seconds) {
    const date = new Date(dateString._seconds * 1000);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
  
  // Handle ISO string dates
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date unavailable';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date unavailable';
  }
};

// Helper: parse a price string like "$34" to a number 34
const parsePriceToNumber = (priceString) => {
  if (!priceString || typeof priceString !== 'string') return NaN;
  const numeric = parseFloat(priceString.replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : NaN;
};

// Helper: format numeric amount to $X (whole dollars)
const formatMoney = (amount) => {
  if (!Number.isFinite(amount)) return '';
  return `$${Math.round(amount)}`;
};

export default function AccountPricingPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [frequency, setFrequency] = useState(pricingData.frequencies[0]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [syncingSubscription, setSyncingSubscription] = useState(false);
  const [error, setError] = useState(null);
  const checkoutInProgress = useRef(false);

  // Get the current user's plan - updated to use planLevel
  const userPlan = user?.planLevel || 'standard';
  const userPlanInterval = user?.planInterval || user?.subscriptionStatus?.currentPlanInterval || null;
  
  // Get subscription status information - updated to match new server structure
  const subscriptionStatus = user?.subscriptionStatus || 'active';
  const subscriptionPeriodEnd = user?.subscriptionPeriodEnd || null;

  // Derive normalized stripe status string from subscriptionStatus (supports string or object)
  const stripeStatus = typeof subscriptionStatus === 'string' 
    ? subscriptionStatus 
    : (subscriptionStatus?.stripeStatus || '');

  // Format dates for display
  const formattedSubscriptionPeriodEnd = subscriptionPeriodEnd ? formatDate(subscriptionPeriodEnd) : null;

  console.log("AccountPricingPage - Current user:", user);
  console.log("AccountPricingPage - Current plan level:", userPlan);
  console.log("AccountPricingPage - Current plan interval:", userPlanInterval);
  console.log("AccountPricingPage - Subscription status:", subscriptionStatus);
  console.log("AccountPricingPage - Subscription period end:", subscriptionPeriodEnd);
  console.log("AccountPricingPage - Formatted subscription period end:", formattedSubscriptionPeriodEnd);

  // Prepare pricing data format for PricingPlans component
  // The component expects mainFeatures but our data has highlights
  const formattedPricingData = {
    ...pricingData,
    tiers: pricingData.tiers.map(tier => ({
      ...tier,
      mainFeatures: tier.highlights // Use highlights as mainFeatures
    }))
  };

  // Derive locked-in price info (only show if user's price is strictly lower than today's price)
  const currentPremiumMonthly = parsePriceToNumber(pricingData.planPricing?.premium?.monthly);
  const currentPremiumAnnual = parsePriceToNumber(ANNUAL_TOTAL.premium); // Get annual total from pricing data
  const userMonthlyPrice = Number.isFinite(user?.monthlyPrice) ? Number(user.monthlyPrice) : NaN;
  const isActiveStripe = Boolean(stripeStatus && stripeStatus !== 'cancelled' && stripeStatus !== 'canceled');
  const userAnnualPrice = Number.isFinite(userMonthlyPrice) ? userMonthlyPrice * 12 : NaN;
  const hasLockedInPrice = (
    user?.planLevel === 'premium' &&
    isActiveStripe &&
    (
      // If on annual plan, compare annual totals
      (userPlanInterval === 'annual' && Number.isFinite(userAnnualPrice) && Number.isFinite(currentPremiumAnnual) && userAnnualPrice > 0 && userAnnualPrice < currentPremiumAnnual) ||
      // Otherwise compare monthly prices
      (userPlanInterval !== 'annual' && Number.isFinite(userMonthlyPrice) && Number.isFinite(currentPremiumMonthly) && userMonthlyPrice > 0 && userMonthlyPrice < currentPremiumMonthly)
    )
  );

  // Set the initial frequency based on user's current plan interval
  useEffect(() => {
    if (userPlanInterval) {
      const matchingFrequency = pricingData.frequencies.find(
        freq => freq.value === (userPlanInterval === 'annual' ? 'annual' : 'monthly')
      );
      if (matchingFrequency) {
        setFrequency(matchingFrequency);
      }
    }
    
    // Preload Stripe script when component mounts
    preloadStripeScript(user);
  }, [user, userPlanInterval]);

  // Handle visibility changes to detect return from Stripe portal
  useEffect(() => {
    // Function to check if we're returning from Stripe portal
    const checkStripePortalReturn = async () => {
      const stripePortalData = localStorage.getItem('stripePortalRedirect');
      
      if (stripePortalData) {
        try {
          const portalData = JSON.parse(stripePortalData);
          const timestamp = portalData.timestamp;
          const now = Date.now();
          const timeDiff = now - timestamp;
          
          // If returning within 10 minutes, sync subscription data
          if (timeDiff < 10 * 60 * 1000) {
            console.log('Detected return from Stripe portal, syncing subscription data');
            
            // Clear the flag immediately to prevent multiple syncs
            localStorage.removeItem('stripePortalRedirect');
            
            // CRITICAL FIX: Clear signup flags to prevent AuthContext from downgrading user
            // When returning from Stripe portal, the user has completed their subscription setup
            if (localStorage.getItem('just_signed_up') === 'true') {
              console.log('Clearing just_signed_up flag on return from Stripe portal');
              localStorage.removeItem('just_signed_up');
            }
            if (sessionStorage.getItem('coming_from_signup') === 'true') {
              console.log('Clearing coming_from_signup flag on return from Stripe portal');
              sessionStorage.removeItem('coming_from_signup');
            }
            
            // Set loading state to show user that something is happening
            setIsLoading(true);
            setSyncingSubscription(true);
            
            try {
              // Sync subscription data
              const syncResult = await syncSubscriptionData(null, false, user);
              console.log('Subscription sync complete:', syncResult);
              
              // If subscription data changed, refresh user data
              if (syncResult.synced && syncResult.subscription) {
                try {
                  // Force refresh of Firebase token to get latest claims
                  await auth.currentUser.getIdToken(true);
                  console.log('Refreshed ID token to get latest claims');
                  
                  // Simply reload the page to get fresh user data
                  // This is the most reliable way to ensure everything is in sync
                  console.log('Reloading page to refresh user data with new subscription info');
                  
                  // Keep the loading modal visible for a moment to ensure user sees it
                  setTimeout(() => {
                    // Reload immediately without showing success message
                    window.location.reload();
                  }, 800);
                } catch (err) {
                  console.error('Error refreshing user data:', err);
                  setError('Failed to update user profile. Please refresh the page.');
                  setSyncingSubscription(false);
                  setIsLoading(false);
                }
              } else {
                // No subscription changes
                setSyncingSubscription(false);
                setIsLoading(false);
              }
            } catch (err) {
              console.error('Error syncing subscription data:', err);
              setError('Failed to update subscription information. Please refresh the page.');
              setSyncingSubscription(false);
              setIsLoading(false);
            }
          } else {
            // Clear old portal redirect data
            localStorage.removeItem('stripePortalRedirect');
          }
        } catch (err) {
          console.error('Error parsing Stripe portal data:', err);
          localStorage.removeItem('stripePortalRedirect');
        }
      }
    };
    
    // Check on initial render
    checkStripePortalReturn();
    
    // Set up visibility change listener
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, checking for Stripe portal return');
        checkStripePortalReturn();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle back button click
  const handleBack = () => {
    navigate('/app/account');
  };

  // Process plan selection
  const processPlanSelection = async (planId) => {
    if (checkoutInProgress.current) return;
    
    checkoutInProgress.current = true;
    setIsLoading(true);
    
    try {
      console.log(`Processing plan selection: ${planId}, interval: ${frequency.value}`);
      
      // Track Meta Pixel InitiateCheckout event
      const selectedTier = pricingData.tiers.find(tier => tier.planId === planId);
      
      // FIXED: Calculate actual amount in cents based on plan and frequency
      let actualAmountCents = null;
      if (selectedTier?.price?.[frequency.value]) {
        const priceString = selectedTier.price[frequency.value];
        if (priceString !== 'Custom') {
          const priceValue = parseFloat(priceString.replace('$', ''));
          actualAmountCents = Math.round(priceValue * 100); // Convert to cents
        }
      }
      
      trackInitiateCheckout({
        content_name: selectedTier?.name || 'Premium Plan',
        content_category: 'Subscription',
        value: actualAmountCents, // FIXED: Use actual amount in cents or null
        content_ids: [planId],
        content_type: 'product',
        planName: selectedTier?.name || 'Premium',
        planInterval: frequency.value === 'annual' ? 'annual' : 'monthly'
      });
      
      // Get current theme
      const isDarkMode = document.documentElement.classList.contains('dark');
      
      // Create a checkout session with Stripe
      const response = await createCheckoutSession(
        planId, 
        frequency.value,
        isDarkMode ? 'dark' : 'light',
        false // Not coming from signup
      );
      
      if (!response || !response.sessionId) {
        throw new Error('No valid session ID returned');
      }
      
      console.log('Created checkout session:', response.sessionId);
      
      // Redirect to Stripe Checkout
      await redirectToCheckout(response.sessionId);
      
      // If execution continues here, something went wrong with the redirect
      console.error('Stripe redirect did not complete - execution continued');
      checkoutInProgress.current = false;
      setIsLoading(false);
      
    } catch (error) {
      console.error('Error processing plan selection:', error);
      setError(error.message || 'Failed to process your request. Please try again.');
      checkoutInProgress.current = false;
      setIsLoading(false);
    }
  };

  // Handle plan selection
  const handleSelectPlan = async (tier) => {
    if (isLoading) return;
    
    // If user already has a paid plan, redirect to billing portal
    if (user?.stripeCustomerId && userPlan !== 'standard' && userPlan !== 'basic') {
      await handleOpenBillingPortal();
      return;
    }
    
    setSelectedPlan(tier);
    setIsLoading(true);
    
    try {
      // Create a checkout session for the selected plan
      await processPlanSelection(tier.planId);
    } catch (error) {
      console.error('Error handling plan selection:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening the Stripe billing portal
  const handleOpenBillingPortal = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Set the return URL to this page
      const returnUrl = `${window.location.origin}/app/account/pricing`;
      
      // Create a timeout that will only trigger if the redirect doesn't happen
      const redirectTimeoutId = setTimeout(() => {
        console.warn('Billing portal redirect timeout - redirect did not complete in expected time');
        setError('Failed to redirect to Stripe billing portal. Please try again.');
        setIsLoading(false);
      }, 10000); // 10 second timeout
      
      // Determine theme but DON'T pass it to the billing portal
      // The Stripe API doesn't accept ui_mode parameter directly
      const isDarkMode = document.documentElement.classList.contains('dark');
      console.log('Current theme is dark mode:', isDarkMode);
      
      // Redirect to Stripe billing portal without theme parameter
      await redirectToBillingPortal(returnUrl);
      
      // The code below should only run if the redirect fails,
      // but we won't show error immediately since the redirect might just be slow
      console.warn('Stripe billing portal redirect initiated but code continued execution');
      
      // Keep loading state until timeout - don't show error immediately
      // The timeout will handle showing the error if needed
      
      // Clean up timeout if we somehow get here (shouldn't happen on successful redirect)
      clearTimeout(redirectTimeoutId);
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setError(error.message || 'Failed to open billing portal.');
      setIsLoading(false);
    }
  };

  // Function to get plan display name
  const getPlanDisplayName = (planLevel) => {
    if (planLevel === 'standard' || planLevel === 'basic') return 'Standard Plan';
    if (planLevel === 'premium') return 'Premium Plan';
    return planLevel.charAt(0).toUpperCase() + planLevel.slice(1) + ' Plan';
  };

  // Custom pricing plans renderer with proper styling
  const renderPricingPlans = (includeRoboCent = false) => {
    return (
      <div className="relative mx-auto grid max-w-md grid-cols-1 gap-10 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-8">
        {pricingData.tiers.filter(tier => tier.planId === 'premium').map((tier) => {
          const isSelected = selectedPlan?.id === tier.id;
          
          // Check if this is the user's current plan by matching both plan level and interval
          const planInterval = frequency.value === 'annual' ? 'annual' : 'monthly';
          const isUserCurrentPlan = userPlan === tier.planId && 
            (!userPlanInterval || userPlanInterval === planInterval);
          
          const isPremiumPlan = tier.planId === 'premium';
          const isEnterprisePlan = false; // Enterprise hidden on account pricing page per new design
          
          // Show "Current Plan" for user's active plan
          let buttonText = 'Select Plan';
          if (isEnterprisePlan) {
            buttonText = tier.contactInfo?.buttonText || 'Contact Sales';
          } else if (isUserCurrentPlan) {
            buttonText = 'Current Plan';
          } else {
            buttonText = 'Manage Subscription';
          }
          
          return (
            <div
              key={tier.id}
              className={classNames(
                tier.featured
                  ? 'z-10 bg-gradient-to-b from-indigo-500 to-purple-600 shadow-xl dark:from-indigo-600 dark:to-purple-700 lg:scale-105 lg:translate-y-0 lg:-translate-x-4'
                  : 'bg-white dark:bg-gray-800 shadow-md',
                'relative rounded-3xl ring-1 ring-gray-200 dark:ring-gray-700 backdrop-blur-sm overflow-hidden',
                isSelected ? 'ring-2 ring-purple-600' : ''
              )}
            >

              
              <div className="p-8 lg:pt-12 xl:p-10 xl:pt-14">
                <h2
                  id={tier.id}
                  className={classNames(
                    tier.featured ? 'text-white' : 'text-gray-900 dark:text-white',
                    'text-2xl font-bold'
                  )}
                >
                  {tier.name}
                </h2>
                <p className={classNames(
                  tier.featured ? 'text-indigo-100' : 'text-gray-600 dark:text-gray-300',
                  'mt-2 text-sm leading-6'
                )}>
                  {tier.description}
                </p>
                
                {/* Show original pricing crossed out for Premium with discount badge */}
                {tier.name === 'Premium' && ORIGINAL_PRICING.premium && (
                  <div className="mt-4 mb-2 flex items-center gap-2">
                    <span className={classNames(
                      tier.featured ? 'text-indigo-100/60' : 'text-gray-500 dark:text-gray-400',
                      'text-lg line-through'
                    )}>
                      {ORIGINAL_PRICING.premium[frequency.value]}
                    </span>
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {PLAN_DISCOUNTS.premium?.label || '30% off'}
                    </span>
                  </div>
                )}
                
                {/* Show discount badge for Enterprise without crossed-out pricing */}
                {tier.name === 'Enterprise' && PLAN_DISCOUNTS.enterprise && (
                  <div className="mt-4 mb-2 flex items-center gap-2">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {PLAN_DISCOUNTS.enterprise?.label || '30% off'}
                    </span>
                  </div>
                )}
                
                <div className="mt-8 flex items-baseline">
                  <p className={classNames(
                      tier.featured ? 'text-white' : 'text-gray-900 dark:text-white',
                      isEnterprisePlan ? 'text-3xl' : 'text-5xl',
                      'font-bold tracking-tight'
                    )}
                  >
                    {tier.price[frequency.value]}
                  </p>
                  <p className={classNames(
                    tier.featured ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400',
                    'ml-2 text-sm'
                  )}>
                    /month
                  </p>
                </div>

                {/* Your locked-in price note on Premium card */}
                
                
                <p className={classNames(
                  tier.featured ? 'text-indigo-100/90' : 'text-gray-500 dark:text-gray-400',
                  'mt-1 text-sm'
                )}>
                  {isEnterprisePlan 
                  ? 'Discounted pricing for more customers' 
                  : frequency.value === 'annual' 
                    ? 'Billed annually (30% off)' 
                    : 'Billed monthly'
                  }
                </p>
                
                {/* Button */}
                <div className="mt-10">
                  {(
                    <button
                      onClick={() => isUserCurrentPlan ? handleOpenBillingPortal() : handleSelectPlan(tier)}
                      disabled={isLoading}
                      className={classNames(
                        tier.featured
                          ? 'bg-white text-purple-600 hover:bg-gray-50 disabled:hover:bg-white'
                          : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 disabled:from-purple-600 disabled:to-indigo-600',
                        'block w-full rounded-xl px-4 py-3.5 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200'
                      )}
                    >
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <ButtonSpinner color="current" />
                          Processing...
                        </div>
                      ) : isUserCurrentPlan ? (
                        'Manage Subscription'
                      ) : (
                        buttonText
                      )}
                    </button>
                  )}
                </div>
                
                {/* Features List */}
                <div className="mt-10 space-y-6">
                  {tier.highlights.map((feature) => (
                    <div key={feature} className="flex gap-x-3">
                      <div className={classNames(
                        tier.featured 
                          ? 'bg-white/20 text-white' 
                          : 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400',
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full'
                      )}>
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </div>
                      <p className={classNames(
                        tier.featured ? 'text-white' : 'text-gray-700 dark:text-gray-300',
                        'text-sm'
                      )}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        {includeRoboCent && (
          <RoboCentSubscriptionsCard isAnnual={frequency?.value === 'annual'} />
        )}
      </div>
    );
  };

  // Informational card linking to RoboCent subscription packages
  const RoboCentSubscriptionsCard = ({ isAnnual = false }) => (
    <div className="w-full h-full">
      <div className="relative rounded-3xl ring-1 ring-gray-200 dark:ring-gray-700 backdrop-blur-sm overflow-hidden bg-white dark:bg-gray-800 shadow-md h-full flex flex-col" style={{ borderColor: 'rgba(240,90,40,0.35)' }}>
        <div className="p-8 lg:pt-12 xl:p-10 xl:pt-14 flex-1 flex flex-col">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(240,90,40,0.12)' }}>
              <img src="/images/logos/robocent.png" alt="RoboCent" className="h-4 w-4 object-contain" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">RoboCent Subscriptions</h3>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Choose an outreach subscription that includes Advocacy Lab plus texting, voice, voter data, and more.</p>
          <div className="mt-6">
            <span className="inline-block mb-2 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(240,90,40,0.12)', color: '#F05A28' }}>
              Subscriptions starting at
            </span>
            <div className="flex items-baseline gap-2">
              <p className="text-5xl font-bold text-gray-900 dark:text-white">{isAnnual ? '$80' : '$97'}</p>
              <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
            </div>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{isAnnual ? 'Billed annually (Jumpstart)' : 'Billed monthly (Jumpstart)'}</p>
          </div>
          <a href="https://docs.robocent.com/explore/pricing/subscription-packages" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" style={{ color: '#F05A28', border: '1px solid rgba(240,90,40,0.35)' }}>View Subscription Options</a>
          <ul className="mt-6 space-y-3 text-sm text-gray-700 dark:text-gray-300">
            {[
              'Text Messaging — SMS/MMS with optional managed sending and 14-day two-way messaging',
              'Voice — robocalls, live transfers, and keypad surveys (with voicemail)',
              'Voter Data — landline, mobile, email, and carrier lookups with appends',
              'Web & Hosting — 10DLC, website hosting, and support; fees often waived on higher tiers',
              'Strategy & Support — expedited onboarding, priority support, and regular audits',
              'Creative — instant campaign graphics and professional voice talent',
            ].map((text) => (
              <li key={text} className="flex items-start gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 mt-0.5" style={{ color: '#F05A28' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Header with back button and title - styled like CampaignDetails */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center text-gray-500 dark:text-gray-400 mb-1">
            <button
              onClick={handleBack}
              className="inline-flex items-center hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">Back to Account</span>
            </button>
          </div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-2xl">
            Manage Your Subscription
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            You are currently on the {getPlanDisplayName(userPlan)} plan.
          </p>
        </div>
      </div>

      {/* Locked-in price banner (full width) */}
      {hasLockedInPrice && (
        <div className="mb-8 w-full rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800/30 dark:bg-primary-900/20">
          <div className="flex items-start gap-3">
            <BadgeCheck className="h-5 w-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <p className="text-sm text-primary-900 dark:text-primary-200">
              {userPlanInterval === 'annual' ? (
                <>
                  <strong>You're locked in at {formatMoney(userMonthlyPrice * 12)}/year.</strong> This is lower than today's price ({formatMoney(currentPremiumAnnual)}/year). Keep your subscription active to retain this rate.
                </>
              ) : (
                <>
                  <strong>You're locked in at {formatMoney(userMonthlyPrice)}/month.</strong> This is lower than today's price ({formatMoney(currentPremiumMonthly)}/month). Keep your subscription active to retain this rate.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800/30 dark:bg-red-900/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 py-10 sm:py-14 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center mb-4">
            <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Choose the right plan for you
            </p>
            <p className="mt-3 text-base text-gray-600 dark:text-gray-400">
              Flexible billing with the ability to cancel anytime - no long-term commitments required.
            </p>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 opacity-50 dark:opacity-50">
              Advocacy Lab is now part of RoboCent / GOTV.com.
            </p>
          </div>
          
          {/* Billing Frequency Switch */}
          <div className="mb-10 flex justify-center">
            <FrequencyToggle
              frequency={frequency}
              setFrequency={setFrequency}
              frequencies={pricingData.frequencies}
            />
          </div>

          {/* Custom Pricing Plans with proper styling - include RoboCent in grid */}
          {renderPricingPlans(true)}

          {/* Support CTA below the two boxes */}
          <div className="mt-10 pt-2 w-full flex justify-center">
            {(() => {
              const calendlyUrl = pricingData.tiers.find(t => t.contactInfo?.calendlyUrl)?.contactInfo?.calendlyUrl || kevinMeetingUrl;
              return (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Have questions?{' '}
                  <a
                    href={calendlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200"
                  >
                    Book a call with our team
                  </a>{' '}
                  to learn more.
                </p>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Disclaimer box - updated to match CopyAnalyzer.jsx style */}
      {userPlan !== 'standard' && userPlan !== 'basic' && (
        <div className="mb-6 mt-8 rounded-lg p-4 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-200/50 dark:border-gray-700/50">
          <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Subscription Information</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            When you cancel your subscription through the Stripe billing portal, you'll continue to have access 
            to your current plan until the end of your billing period. After that, your subscription will be removed.
            All subscription management is handled through Stripe's secure portal, including changing plans, updating payment methods, 
            or cancelling your subscription. Your data is protected, and you have full control over your billing preferences at all times.
            For immediate assistance with billing issues, please contact our support team.
          </p>
        </div>
      )}
      
      {/* Loading Modal for subscription sync */}
      <LoadingModal 
        isOpen={syncingSubscription} 
        message="Updating your subscription information..."
      />
      
      <Footer />
    </div>
  );
} 